import { NavLink, Navigate, Outlet, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@repo/ui/src/lib/utils';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { PageHeader } from '../../components/PageHeader';
import { useAuthStore } from '../../stores/authStore';
import { defaultSectionForRole, isSettingsSection, settingsSectionsForRole, useIsSettingsNarrow } from './settingsNav';

/** `/settings/:section` shell: rail + content column at 1024px and up, back-header
 *  + content below that (see SETTINGS_NARROW_MAX_PX for why the threshold is not
 *  the app-wide mobile breakpoint). An unknown section or one the role can't see
 *  redirects to that role's default section. */
export function SettingsLayout() {
  const { t } = useTranslation('settings');
  const role = useAuthStore((s) => s.role);
  const isMobile = useIsMobile();
  const isNarrow = useIsSettingsNarrow(isMobile);
  const { section } = useParams();
  const groups = settingsSectionsForRole(role);

  if (section && !isSettingsSection(section, role)) {
    return <Navigate replace to={`/settings/${defaultSectionForRole(role)}`} />;
  }

  const current = groups.flatMap((g) => g.items).find((i) => i.key === section);

  if (isNarrow) {
    return (
      <div className="p-6 max-w-2xl mx-auto w-full">
        {current ? (
          <PageHeader
            size="sm"
            back={{ label: t('back'), to: '/settings' }}
            title={t(current.label.key)}
            subtitle={t(`sections.${current.key}.desc`)}
          />
        ) : (
          <PageHeader size="sm" title={t('title')} subtitle={t('subtitle')} />
        )}
        <Outlet />
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto w-full">
      <PageHeader size="lg" title={t('title')} subtitle={t('subtitle')} />
      <div className="flex gap-8">
        <nav aria-label={t('title')} className="w-56 shrink-0 sticky top-4 self-start space-y-[28px]">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="px-[12px] pb-[8px] text-[11px] font-semibold uppercase tracking-wider text-surface-400">
                {t(g.label.key)}
              </div>
              <ul className="space-y-[2px]">
                {g.items.map((i) => (
                  <li key={i.key}>
                    <NavLink
                      to={`/settings/${i.key}`}
                      className={({ isActive }) =>
                        cn(
                          // Pinned in px: --spacing is 0.2rem here, so h-8/px-3 gave a 25px row.
                          'flex h-[38px] items-center gap-[10px] rounded-lg px-[12px] text-[14px]',
                          isActive ? 'bg-brand-50 font-semibold text-brand-600' : 'text-surface-600 hover:bg-surface-100',
                        )
                      }
                    >
                      <i.icon className="size-[16px]" strokeWidth={1.8} />
                      {t(i.label.key)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="min-w-0 max-w-[640px] flex-1">
          {current && (
            <div className="mb-5">
              <h2 className="font-display text-xl font-semibold text-surface-900">{t(current.label.key)}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t(`sections.${current.key}.desc`)}</p>
            </div>
          )}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
